// Design System - Bannières Contextuelles Premium
// Chaque univers a ses propres couleurs et textes
// Composant réutilisable sur tous les univers de la plateforme

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wrench, ShoppingBag, Users, MessageSquare, Briefcase, Building2, Settings,
  ChevronRight
} from 'lucide-react';

// Configuration du contenu par univers avec couleurs UNIFORMISÉES (Upgrade Licorne)
// Toutes les bannières utilisent le même fond #EEF2FF + bordure orange #E8650A
const BANNER_CONTENT = {
  artisans: {
    title: 'Rejoins les artisans certifiés',
    subtitle: 'Crée ton profil pro — sois visible dès le lancement',
    microline: '✓ Gratuit pendant la Beta · ✓ 1 an offert aux membres fondateurs',
    cta: 'Créer mon profil gratuit ›',
    ctaPath: '/pro/fondateur',
    icon: Wrench,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
  marketplace: {
    title: 'Ouvre ta boutique en ligne',
    subtitle: 'Vends tes produits sans commission, directement à tes clients',
    microline: '✓ 0% commission · ✓ Gratuit pendant la Beta',
    cta: 'Ouvrir ma boutique ›',
    ctaPath: '/pro/fondateur',
    icon: ShoppingBag,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
  particuliers: {
    title: 'Dépose ton annonce gratuitement',
    subtitle: 'Vends ou achète facilement entre particuliers',
    microline: '✓ Gratuit · ✓ Sans engagement · ✓ Annonce en 2 minutes',
    cta: 'Déposer une annonce ›',
    ctaPath: '/annonces/deposer',
    icon: Users,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
  reseau: {
    title: 'Rejoins la communauté du bâtiment',
    subtitle: 'Partage tes chantiers, trouve des opportunités, développe ton réseau',
    microline: '✓ 1 compte certifié · ✓ Réseau 100% vérifié',
    cta: 'Créer mon compte ›',
    ctaPath: '/pro/fondateur',
    icon: MessageSquare,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
  emploi: {
    title: 'Publie tes offres d\'emploi',
    subtitle: 'Trouve les bons profils dans le BTP — gratuit pendant la Beta',
    microline: '✓ Offres illimitées · ✓ Candidats qualifiés BTP',
    cta: 'Déposer une offre ›',
    ctaPath: '/pro/fondateur',
    icon: Briefcase,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
  'reseau-pros': {
    title: 'Développe ton réseau professionnel',
    subtitle: 'Achat groupé, sous-traitance, opportunités business entre pros',
    microline: '✓ Centrale d\'achat · ✓ Pros vérifiés uniquement',
    cta: 'Rejoindre le réseau pro ›',
    ctaPath: '/pro/fondateur',
    icon: Building2,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
  'pro': {
    title: 'Pilote ton activité efficacement',
    subtitle: 'Cockpit, devis, facturation et gestion centralisée',
    microline: '✓ Tout-en-un · ✓ Simple et puissant',
    cta: 'Accéder à mon espace ›',
    ctaPath: '/pro/fondateur',
    icon: Settings,
    colors: {
      background: '#EEF2FF',
      borderLeft: '#E8650A',
      title: '#0F172A',
      subtitle: '#374151',
      button: '#1a2a4a',
      buttonHover: '#E8650A',
    }
  },
};

export default function UniversalBanner({ variant = 'artisans', className = '' }) {
  const content = BANNER_CONTENT[variant] || BANNER_CONTENT.artisans;
  const { colors } = content;

  // Bannière visible uniquement sur mobile (< 640px)
  // Cachée sur tablette et desktop
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`w-full sm:hidden ${className}`}
      data-testid={`universal-banner-${variant}`}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        <div 
          className="relative overflow-hidden rounded-2xl px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6"
          style={{
            backgroundColor: colors.background,
            borderLeft: `4px solid ${colors.borderLeft}`,
          }}
        >
          {/* Content container */}
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 pl-2">
            
            {/* Text section */}
            <div className="flex-1 min-w-0">
              <h3 
                className="font-bold text-lg lg:text-xl leading-tight mb-1"
                style={{ color: colors.title }}
              >
                {content.title}
              </h3>
              <p 
                className="text-sm lg:text-base leading-relaxed mb-1"
                style={{ color: colors.subtitle }}
              >
                {content.subtitle}
              </p>
              {content.microline && (
                <p className="text-xs text-gray-500 mt-1">
                  {content.microline}
                </p>
              )}
            </div>

            {/* CTA Button - Upgrade Licorne style */}
            <Link 
              to={content.ctaPath}
              className="flex-shrink-0 w-full sm:w-auto"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto h-11 sm:h-10 px-5 lg:px-6 text-white font-semibold text-sm rounded-[10px] flex items-center justify-center gap-1 transition-all duration-200 shadow-md"
                style={{ 
                  backgroundColor: colors.button,
                  boxShadow: '0 4px 12px rgba(26,42,74,0.25)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.buttonHover;
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(232,101,10,0.35)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.button;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(26,42,74,0.25)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                data-testid={`banner-cta-${variant}`}
              >
                <span>{content.cta}</span>
              </motion.button>
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Export des variants pour documentation
export const BANNER_VARIANTS = Object.keys(BANNER_CONTENT);
